import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, throttle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Throttled scroll handler reference for cleanup
   * @type {(() => void) | null}
   */
  #throttledScrollHandler = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        const newState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.stickyState !== newState) {
          this.dataset.stickyState = newState;
          changeMetaThemeColor(this.refs.headerRowTop);
        }
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // For scroll-up mode, check if we've scrolled past the initial position.
    // No layout read (getBoundingClientRect) on every scroll event: the header sits at the top of
    // the page, so "header top < 0" can only happen once scrollTop > 0, which this already covers.
    const hasScrolledPastTop = scrollTop > 0;

    if (hasScrolledPastTop) {
      this.#offscreen = true;
    }

    // The header's position is only needed when scrolling up within 10px of the top. Measure it
    // only then, and before any attribute below is written (reading layout after a write would
    // force a synchronous style/layout recalculation).
    const isHeaderAtNaturalTop =
      isScrollingUp && scrollTop <= 10 && this.getBoundingClientRect().top >= 0;

    // Only process scroll behavior if header is offscreen or we've scrolled
    if (!this.#offscreen && !hasScrolledPastTop && stickyMode !== 'always') {
      // At top, ensure header is visible
      // If collection has no banner media, keep it active (non-transparent)
      const newState = this.#hasNoBannerMedia() ? 'active' : 'inactive';
      if (this.dataset.stickyState !== newState) {
        this.dataset.stickyState = newState;
      }
      this.dataset.scrollDirection = 'none';
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isHeaderAtNaturalTop) {
        // reset sticky state when header is scrolled up to natural position (near top)
        // BUT: if collection has no banner media, keep it active (non-transparent)
        this.#offscreen = false;
        const newState = this.#hasNoBannerMedia() ? 'active' : 'inactive';
        if (this.dataset.stickyState !== newState) {
          this.dataset.stickyState = newState;
        }
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        if (this.dataset.stickyState !== 'active') {
          this.dataset.stickyState = 'active';
        }
        this.dataset.scrollDirection = 'up';
      }
    } else if (scrollTop > this.#lastScrollTop) {
      // Scrolling down - hide header
      if (this.dataset.stickyState === 'active') {
        this.dataset.scrollDirection = 'down';
        // delay transitioning to idle hidden state for hiding animation
        this.setAttribute('data-animating', '');

        this.#timeout = setTimeout(() => {
          this.dataset.stickyState = 'idle';
          this.removeAttribute('data-animating');
        }, this.#animationDelay);
      } else {
        if (this.dataset.stickyState !== 'idle') {
          this.dataset.stickyState = 'idle';
        }
        this.dataset.scrollDirection = 'down';
      }
    } else {
      // At top or no scroll change
      if (scrollTop <= 10) {
        this.#offscreen = false;
        // If collection has no banner media, keep header active (non-transparent)
        const newState = this.#hasNoBannerMedia() ? 'active' : 'inactive';
        if (this.dataset.stickyState !== newState) {
          this.dataset.stickyState = newState;
        }
        this.dataset.scrollDirection = 'none';
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Checks if collection has no banner media (should keep header non-transparent)
   * @returns {boolean}
   */
  #hasNoBannerMedia() {
    return document.documentElement.classList.contains('jit-collection-no-banner-media');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        // Initialize scroll position
        this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
        
        // Set initial state based on scroll position
        if (stickyMode === 'scroll-up') {
          const scrollTop = this.#lastScrollTop;
          if (scrollTop <= 10) {
            this.dataset.stickyState = 'inactive';
            this.dataset.scrollDirection = 'none';
            this.#offscreen = false;
          } else {
            this.#offscreen = true;
            this.dataset.stickyState = 'idle';
            this.dataset.scrollDirection = 'none';
          }
        }
        
        // Throttle scroll handler to prevent long animation frames
        // Using throttle with requestAnimationFrame timing (~16ms at 60fps)
        const throttledScrollHandler = throttle(this.#handleWindowScroll.bind(this), 16);
        document.addEventListener('scroll', throttledScrollHandler, { passive: true });
        // Store reference for cleanup
        this.#throttledScrollHandler = throttledScrollHandler;
      }
    }
    
    // Header background brightness: data-bg-light is printed by sections/header.liquid (no runtime
    // getComputedStyle check, ResizeObserver or MutationObserver needed).

    // Add hover effect only on devices that actually support hover (desktop / pointer: fine).
    // On touch devices, synthetic mouseenter/mouseleave events can fire on tap and hurt INP,
    // so we completely skip this behavior there.
    const canHover =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (canHover) {
      // Kept in JS rather than CSS :hover: a CSS version would have to mirror ~170
      // [data-sticky-state="active"] selectors across 6 files (header, drawers, search, menu), several
      // of which compete with "inactive" rules. This is one attribute write per enter/leave, desktop only.
      /**
       * Sets the sticky state for the hover effect, skipping writes when the value is unchanged so
       * moving the mouse in/out doesn't invalidate styles for nothing.
       * @param {string} state - The state to set ('active' or 'inactive')
       */
      const setStickyState = (state) => {
        // If collection has no banner media, keep header active even on mouseleave
        const nextState = state === 'inactive' && this.#hasNoBannerMedia() ? 'active' : state;
        if (this.getAttribute('data-sticky-state') !== nextState) {
          this.setAttribute('data-sticky-state', nextState);
        }
      };

      this.addEventListener('mouseenter', () => setStickyState('active'));
      // Only return to the transparent "top of page" look when the page really is at the top (same
      // 10px threshold as the scroll handler). Mid-page the sticky header stays solid; it used to be
      // set to 'inactive' (transparent) on every mouseleave, even halfway down the page.
      this.addEventListener('mouseleave', () => {
        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        if (scrollTop <= 10) setStickyState('inactive');
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    if (this.#throttledScrollHandler) {
      document.removeEventListener('scroll', this.#throttledScrollHandler);
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));
    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
